import Image from 'next/image';

import Link from 'next/link';
import { useFormatter } from 'next-intl';

import { CurvedHalfCircle } from '@/app/_componentsv2/shapes/curved-half-circle';
import { ApiSummaryProject } from '@/lib/core-api/types';

import {
  projectCategoryTag,
  projectDescription,
  projectFiscality,
  projectIllustration,
  projectIllustrationImg,
  projectIllustrationProgress,
  projectIllustrationProgressCircle,
  projectIllustrationProgressShape,
  projectLogo,
  projectMaturityTag,
  projectSlide,
  projectStatus,
  projectStatusInvested,
  projectStatusLabel,
  projectStatusProgress,
  projectStatusProgressWrapper,
  projectTags,
} from './index.css';

type ProjectCardProps = {
  project: ApiSummaryProject;
};

export function ProjectCard({ project }: ProjectCardProps) {
  const format = useFormatter();

  return (
    <Link
      href={`/projects/${project.slug}`}
      key={project.slug}
      className={projectSlide}
    >
      <section className={projectIllustration}>
        {project.bgImgUrl !== null && (
          <Image
            className={projectIllustrationImg}
            role="presentation"
            alt={project.projectName}
            src={project.bgImgUrl}
            width={310}
            height={470}
          />
        )}
        <section className={projectIllustrationProgress}>
          <CurvedHalfCircle className={projectIllustrationProgressShape} />
          <span className={projectIllustrationProgressCircle}>
            {Math.round((project.amountInvested / project.totalAmount) * 100)}%
          </span>
        </section>
      </section>
      <section className={projectTags}>
        <span
          className={projectCategoryTag}
          dangerouslySetInnerHTML={{ __html: project.category.label }}
        />
        <span
          className={projectMaturityTag}
          dangerouslySetInnerHTML={{ __html: project.maturity ?? '' }}
        />
      </section>

      {project.imgUrl !== null && (
        <Image
          className={projectLogo}
          role="presentation"
          alt={project.projectName}
          src={project.imgUrl}
          width={240}
          height={50}
        />
      )}

      <section className={projectStatus}>
        <span className={projectStatusInvested}>
          {format.number(project.amountInvested, {
            style: 'currency',
            useGrouping: 'always',
            currency: 'EUR',
          })}
        </span>
        <div role="display" className={projectStatusProgressWrapper}>
          <div
            className={projectStatusProgress}
            style={{
              width: `${(project.amountInvested / project.totalAmount) * 100}%`,
            }}
          ></div>
        </div>
        <span className={projectStatusLabel}>Campagne en cours</span>
      </section>
      <section
        className={projectDescription}
        dangerouslySetInnerHTML={{
          __html: project.smallDescription ?? '',
        }}
      />
      <section className={projectFiscality}>
        {project.taxElements?.map((tax) => <span key={tax}>{tax}</span>)}
      </section>
    </Link>
  );
}
